/**
 * Estado global de sesión.
 *
 * - Única fuente de verdad sobre el usuario y sus tokens.
 * - Persistido para sobrevivir recargas (sólo `user` y `tokens`).
 * - Implementa `SessionProvider`, de modo que la capa HTTP puede leer el token y
 *   renovarlo sin conocer este store.
 */

import { create } from 'zustand';
import { createJSONStorage, persist } from 'zustand/middleware';

import { registerSessionProvider } from '@/lib/http/session-provider';
import { storage } from '@/lib/storage';

import { authApi } from './auth.api';
import type { AuthStatus, AuthTokens, AuthUser, LoginResponse } from './types';

interface AuthState {
  user: AuthUser | null;
  tokens: AuthTokens | null;
  status: AuthStatus;
  /** `true` cuando la sesión se cerró por expiración (se muestra aviso en login). */
  sessionExpired: boolean;

  setSession: (session: LoginResponse) => void;
  setUser: (user: AuthUser) => void;
  setAuthenticating: () => void;
  clearSession: (options?: { expired?: boolean }) => void;
  acknowledgeExpiration: () => void;

  /** ¿El usuario pertenece a alguno de los grupos indicados? */
  hasAnyRole: (...roles: string[]) => boolean;
}

const PERSIST_KEY = 'auth-session';

/** Storage compatible con zustand, con fallback en memoria. */
const zustandStorage = createJSONStorage(() => ({
  getItem: (name: string) => storage.getRaw(name),
  setItem: (name: string, value: string) => {
    storage.setRaw(name, value);
  },
  removeItem: (name: string) => {
    storage.remove(name);
  },
}));

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      tokens: null,
      status: 'idle',
      sessionExpired: false,

      setSession: ({ user, tokens }) => {
        set({ user, tokens, status: 'authenticated', sessionExpired: false });
      },

      setUser: (user) => {
        set({ user });
      },

      setAuthenticating: () => {
        set({ status: 'authenticating' });
      },

      clearSession: (options) => {
        set({
          user: null,
          tokens: null,
          status: 'anonymous',
          sessionExpired: Boolean(options?.expired),
        });
      },

      acknowledgeExpiration: () => {
        set({ sessionExpired: false });
      },

      hasAnyRole: (...roles) => {
        if (roles.length === 0) return true;

        const currentRoles = get().user?.roles ?? [];
        return roles.some((role) => currentRoles.includes(role));
      },
    }),
    {
      name: PERSIST_KEY,
      storage: zustandStorage,
      // Sólo persistimos la sesión; `status` y `sessionExpired` son efímeros.
      partialize: (state) => ({ user: state.user, tokens: state.tokens }),
      onRehydrateStorage: () => (state, error) => {
        if (error) {
          console.warn('[auth] No se pudo restaurar la sesión:', error);
          return;
        }

        if (!state) return;

        // La rehidratación se ejecuta mientras el store todavía se está creando,
        // por lo que `useAuthStore` aún no existe: diferimos el ajuste de `status`
        // un microtask para evitar un ReferenceError.
        queueMicrotask(() => {
          useAuthStore.setState({
            status: state.tokens?.accessToken ? 'authenticated' : 'anonymous',
          });
        });
      },
    },
  ),
);

// ------------------------------------------------------------------
// Integración con la capa HTTP
// ------------------------------------------------------------------

/** Evita disparar varios refresh simultáneos (varias requests en 401 a la vez). */
let inFlightRefresh: Promise<boolean> | null = null;

async function refreshSession(): Promise<boolean> {
  if (inFlightRefresh) return inFlightRefresh;

  // La cookie httpOnly lleva el refresh token: no hace falta tenerlo en memoria.
  inFlightRefresh = (async () => {
    try {
      const session = await authApi.refresh();
      useAuthStore.getState().setSession(session);
      return true;
    } catch {
      useAuthStore.getState().clearSession({ expired: true });
      return false;
    } finally {
      inFlightRefresh = null;
    }
  })();

  return inFlightRefresh;
}

/**
 * Conecta el store con `api-client`. Debe invocarse una sola vez al arrancar
 * la aplicación (ver `app/providers.tsx`).
 */
export function registerAuthSessionProvider(): void {
  registerSessionProvider({
    getAccessToken: () => useAuthStore.getState().tokens?.accessToken ?? null,
    canRefresh: () => Boolean(useAuthStore.getState().tokens?.accessToken),
    refresh: refreshSession,
    onSessionExpired: () => {
      useAuthStore.getState().clearSession({ expired: true });
    },
  });
}

// ------------------------------------------------------------------
// Selectores (atómicos: evitan re-renders innecesarios)
// ------------------------------------------------------------------

export const selectUser = (state: AuthState): AuthUser | null => state.user;
export const selectIsAuthenticated = (state: AuthState): boolean =>
  Boolean(state.tokens?.accessToken) && state.status !== 'anonymous';
export const selectIsAdmin = (state: AuthState): boolean => Boolean(state.user?.isAdmin);
export const selectRoles = (state: AuthState): string[] => state.user?.roles ?? [];
